import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ImageOff, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/season3-import — bulk Excel importer (§5-§8 of the Season 3 spec).
 *
 * Admin drops in the Google-Forms-exported PLAYERS LIST.xlsx and gets a
 * live summary panel showing:
 *   - total rows / created / updated / duplicates
 *   - photos_ok / photos_failed / photos_needs_review
 *   - missing_ranking / missing_category counts
 *   - per-row errors
 */
export default function Season3Import() {
  const [file, setFile] = useState(null);
  const [fetchPhotos, setFetchPhotos] = useState(true);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const inputRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx") && !f.name.toLowerCase().endsWith(".xls")) {
      toast.error("Please pick an Excel (.xlsx) file");
      return;
    }
    setFile(f);
    setSummary(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setSummary(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/players/import-season3-excel?fetch_photos=${fetchPhotos}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setSummary(data);
      toast.success(`Imported ${data.created + data.updated} players`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="season3-import">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Season 3 · Bulk Import</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
          Excel<br/><span className="text-[#F4B400]">Import.</span>
        </h1>
        <div className="mt-3 text-zinc-400 text-sm max-w-2xl">
          Drop your Google-Forms-exported <b>PLAYERS LIST.xlsx</b> below. Player records are created/updated
          and Google Drive photos are auto-fetched where the sharing link is public.
        </div>
        {/* FIP ranking reference — helps admins look up international players'
            live padel rankings before assigning category / current_ranking. */}
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <a
            href="https://www.padelfip.com/rankings/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fip-ranking-link"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#11241b] border border-[#F4B400]/40 text-[#F4B400] hover:bg-[#F4B400]/10 text-xs font-bold uppercase tracking-widest transition"
          >
            <ExternalLink className="w-3.5 h-3.5" /> FIP World Rankings
          </a>
          <a
            href="https://www.padelfip.com/players/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fip-players-link"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#11241b] border border-white/10 text-zinc-300 hover:border-white/30 text-xs font-bold uppercase tracking-widest transition"
          >
            <ExternalLink className="w-3.5 h-3.5" /> FIP Player Search
          </a>
          <span className="text-[10px] text-zinc-500 italic">
            Look up A+ international players&apos; live FIP rank before assigning category
          </span>
        </div>
      </div>

      {/* Drop zone */}
      <Card className="p-8 bg-[#11241b] border-white/10 mb-6">
        <div
          className="border-2 border-dashed border-white/20 rounded-xl p-10 text-center cursor-pointer hover:border-[#F4B400]/60 transition-all"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          data-testid="dropzone"
        >
          <FileSpreadsheet className="w-12 h-12 mx-auto text-[#F4B400] mb-3" />
          <div className="font-anton text-2xl uppercase tracking-tight text-white">
            {file ? file.name : "Drop your .xlsx here"}
          </div>
          <div className="text-xs text-zinc-500 mt-2">or click to browse — max 15MB</div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="file-input"
          />
        </div>

        <div className="flex items-center justify-between mt-6">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={fetchPhotos}
              onChange={(e) => setFetchPhotos(e.target.checked)}
              data-testid="fetch-photos-toggle"
            />
            Fetch Google Drive photos (adds ~1-3s per player)
          </label>
          <Button
            onClick={upload}
            disabled={!file || busy}
            className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]"
            data-testid="import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {busy ? "Importing…" : "Start Import"}
          </Button>
        </div>
      </Card>

      {/* Summary panel */}
      {summary && (
        <Card className="p-6 bg-[#0f1a15] border-white/10" data-testid="summary-panel">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-[#00A86B]" />
            <div className="font-anton text-2xl uppercase tracking-tight text-white">Import Summary</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Total Rows" value={summary.total_rows} testid="stat-total" />
            <Stat label="Created" value={summary.created} accent="#00A86B" testid="stat-created" />
            <Stat label="Updated" value={summary.updated} accent="#F4B400" testid="stat-updated" />
            <Stat label="Duplicates" value={summary.duplicates} testid="stat-duplicates" />
            <Stat label="Photos OK" value={summary.photos_ok} accent="#00A86B" testid="stat-photos-ok" />
            <Stat label="Photos Failed" value={summary.photos_failed} accent="#E32424" testid="stat-photos-failed" />
            <Stat label="Missing Ranking" value={summary.missing_ranking} accent="#F4B400" testid="stat-missing-rank" />
            <Stat label="Missing Category" value={summary.missing_category} accent="#F4B400" testid="stat-missing-cat" />
          </div>
          {summary.photos_needs_review?.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <ImageOff className="w-4 h-4 text-amber-500" />
                <div className="text-sm font-bold text-amber-500 uppercase tracking-widest">
                  Photos need review ({summary.photos_needs_review.length})
                </div>
              </div>
              <div className="max-h-64 overflow-auto rounded border border-white/10 divide-y divide-white/10">
                {summary.photos_needs_review.slice(0, 20).map((p, i) => (
                  <div key={i} className="p-2 text-xs bg-black/20 flex items-center justify-between gap-2">
                    <span className="text-white truncate">{p.name}</span>
                    <span className="text-zinc-500">{p.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {summary.errors?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <div className="text-sm font-bold text-red-500 uppercase tracking-widest">
                  Errors ({summary.errors.length})
                </div>
              </div>
              <div className="max-h-40 overflow-auto text-xs font-mono text-zinc-400 bg-black/30 p-2 rounded">
                {summary.errors.slice(0, 10).map((er, i) => (
                  <div key={i}>Row {er.row}: {er.error}</div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 p-3 bg-[#F4B400]/10 border border-[#F4B400]/40 rounded-lg text-xs text-[#F4B400] flex items-center gap-2">
            <Users className="w-4 h-4" />
            Next step: open Players → assign categories + rankings for the {summary.missing_category} players missing them.
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "#94A3B8", testid }) {
  return (
    <div className="p-3 rounded-lg bg-[#11241b] border border-white/10" style={{ borderLeft: `3px solid ${accent}` }} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-3xl text-white leading-none mt-1">{value ?? 0}</div>
    </div>
  );
}
